import { IChat, IMessage, IProject, IPrompt, ISummary } from "@/lib/types";
import { v7 as uuidv7 } from "uuid";

export const generateMockChats = (count = 50): IChat[] => {
    const now = new Date();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    return Array(count)
        .fill(0)
        .map((_itm, idx) => ({
            chatid: uuidv7(),
            user: "mbeeson@fender.com",
            title: `Test Title Very Long ${idx} Additional Title Length`,
            favorite: Math.random() < 0.1,
            shareid: undefined,
            created: yesterday,
            lastmessage: now,
        }));
};

export const generateMockProjects = (count = 10): IProject[] => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    return Array(count)
        .fill(0)
        .map((_itm, idx) => ({
            projectid: uuidv7(),
            user: "mbeeson@fender.com",
            title: `Test Project ${idx} Very Long Title That Keeps Going`,
            favorite: Math.random() < 0.2,
            description: `Description for project ${idx}`,
            instructions: `Instructions for project ${idx}`,
            chats: [],
            created: yesterday,
        }));
};

export const generateMockMessages = (): IMessage[] => {
    return [
        {
            messageid: uuidv7(),
            role: "user",
            content: [
                {
                    text: "Hello, can you help me with a coding question?",
                },
            ],
        },
        {
            messageid: uuidv7(),
            role: "assistant",
            content: [
                {
                    reasoningContent: {
                        reasoningText: {
                            text: "The user is asking for help with a coding question. I should respond positively and ask for more specific details about what they need help with. This will help me provide more targeted assistance.",
                        },
                    },
                },
                {
                    text: "Of course! I'd be happy to help you with your coding question. What specific programming challenge are you working on?",
                },
            ],
        },
        {
            messageid: uuidv7(),
            role: "user",
            content: [
                {
                    text: "I'm trying to understand how async/await works in JavaScript.",
                },
            ],
        },
        {
            messageid: uuidv7(),
            role: "assistant",
            content: [
                {
                    reasoningContent: {
                        reasoningText: {
                            text: "The user is asking about async/await in JavaScript. This is a fundamental concept for handling asynchronous operations. I should:\n1. Explain what async/await does conceptually\n2. Break down the key points clearly\n3. Offer to provide practical examples\n4. Keep the explanation accessible but thorough",
                        },
                    },
                },
                {
                    text: "Great question! Async/await is a way to handle asynchronous operations in JavaScript. Here's how it works:\n\n1. `async` functions always return a Promise\n2. `await` pauses execution until the Promise resolves\n3. It makes asynchronous code look synchronous\n\nWould you like me to show you some practical examples?",
                },
            ],
        },
    ];
};

export const generateMockPrompts = (): IPrompt[] => {
    return [
        {
            title: "Meeting Scheduler",
            prompt: "Help me write a professional email to schedule a meeting. Include the purpose, proposed times, and any preparation needed:\n\nPlease provide the following details first:\n- Meeting purpose/objective:\n- Attendees (roles/departments):\n- Estimated duration:\n- Preferred time frame:\n- Meeting format (in-person/virtual):\n- Any materials to review beforehand:\n\nThe email should include:\n- Clear, professional subject line\n- Brief meeting purpose and agenda\n- 2-3 proposed time slots with time zones\n- Expected duration and format (Zoom, in-person, etc.)\n- Any preparation materials or pre-reading\n- Request for confirmation and alternative times if needed\n- Contact information for questions\n\nTone should be professional yet friendly and accommodate different schedules.\n\n[Provide your meeting details above]",
            user: "mbeeson@fender.com",
            category: "Communication",
        },
        {
            title: "Status Update Writer",
            prompt: "Draft a comprehensive project status update for stakeholders. Include progress made, current challenges, next steps, and timeline:\n\nBefore drafting, please provide:\n- Project name and phase:\n- Reporting period (week/month):\n- Key stakeholders/audience:\n- Major milestones or deliverables:\n- Current status (on track/behind/ahead):\n\nThe status update should include these sections:\n\n**Executive Summary** (2-3 sentences)\n- Overall project health and key highlights\n\n**Progress This Period**\n- Completed milestones and deliverables\n- Key metrics or KPIs achieved\n- Team accomplishments\n\n**Current Challenges & Risks**\n- Obstacles encountered and their impact\n- Risk mitigation strategies\n- Resource or dependency issues\n\n**Next Steps & Priorities**\n- Immediate priorities for next period\n- Upcoming milestones and deadlines\n- Required decisions or approvals\n\n**Timeline & Budget**\n- Updated project timeline\n- Budget status if applicable\n- Any changes to original scope\n\nUse clear, concise language appropriate for executive-level audience.\n\n[Provide your project details above]",
            user: "mbeeson@fender.com",
            category: "Communication",
        },
        {
            title: "Document Summarizer",
            prompt: "Create a concise summary of the key points, decisions, and action items from the following document or meeting notes:\n\nSummary Requirements:\n- Maximum 300 words for the main summary\n- Structure with clear sections:\n  * **Key Decisions Made**\n  * **Important Discussion Points**\n  * **Action Items** (with owners and deadlines if mentioned)\n  * **Next Steps**\n  * **Follow-up Required**\n\nFor each action item, include:\n- What needs to be done\n- Who is responsible (if specified)\n- When it's due (if mentioned)\n- Any dependencies or prerequisites\n\nPrioritize:\n1. Concrete decisions and commitments\n2. Actionable items with clear ownership\n3. Important context that affects future work\n4. Unresolved issues that need follow-up\n\nUse bullet points and clear formatting for easy scanning.\n\n[Paste your document or meeting notes here]",
            user: "mbeeson@fender.com",
            category: "Communication",
        },
        {
            title: "Task Breakdown",
            prompt: "Break down this complex problem or project into smaller, manageable tasks. Organize them by priority and dependencies:\n\nPlease first describe:\n- The overall goal or problem to solve:\n- Available resources (time, people, budget):\n- Key constraints or limitations:\n- Success criteria or definition of done:\n\nTask breakdown should include:\n\n**Phase 1: Planning & Setup**\n- Research and analysis tasks\n- Setup and preparation work\n- Stakeholder alignment\n\n**Phase 2: Core Implementation**\n- Primary development or execution tasks\n- Testing and validation work\n- Integration points\n\n**Phase 3: Completion & Delivery**\n- Final testing and quality assurance\n- Documentation and handoff\n- Launch or deployment activities\n\nFor each task, provide:\n- Clear description of what needs to be done\n- Estimated effort (hours/days)\n- Priority level (High/Medium/Low)\n- Dependencies on other tasks\n- Required skills or resources\n- Acceptance criteria\n\nHighlight critical path items and potential bottlenecks.\n\n[Describe your complex problem or project here]",
            user: "mbeeson@fender.com",
            category: "Planning",
        },
        {
            title: "Timeline Creator",
            prompt: "Create a detailed project timeline with milestones, deliverables, and dependencies. Include realistic time estimates:\n\nFirst, provide these project details:\n- Project scope and objectives:\n- Team size and key roles:\n- Available working hours per week:\n- Hard deadlines or constraints:\n- Risk factors that might cause delays:\n\nTimeline should include:\n\n**Project Phases**\n- Discovery & Planning (Week 1-X)\n- Design & Development (Week X-Y)\n- Testing & Quality Assurance (Week Y-Z)\n- Deployment & Launch (Week Z+)\n\n**For each phase, specify:**\n- Key milestones with specific dates\n- Major deliverables and acceptance criteria\n- Resource allocation and responsibilities\n- Dependencies between tasks\n- Buffer time for risks and unknowns\n\n**Critical Path Analysis**\n- Tasks that cannot be delayed without affecting final deadline\n- Parallel work opportunities\n- Resource conflicts or bottlenecks\n\n**Risk Mitigation**\n- Contingency plans for common delays\n- Alternative approaches if primary plan fails\n- Regular checkpoint and review dates\n\nProvide timeline in both Gantt chart format (text-based) and milestone list.\n\n[Describe your project scope and constraints here]",
            user: "mbeeson@fender.com",
            category: "Planning",
        },
        {
            title: "Decision Analyzer",
            prompt: "Analyze the pros and cons of different approaches to solve this problem. Consider factors like cost, time, resources, and risks:\n\nFirst, clearly define:\n- The decision you need to make:\n- Available options (list 2-4 alternatives):\n- Key stakeholders affected:\n- Success criteria and constraints:\n- Timeline for making this decision:\n\n**Analysis Framework:**\n\nFor each option, evaluate:\n\n**Implementation Feasibility**\n- Technical complexity and requirements\n- Resource needs (people, budget, tools)\n- Timeline to complete\n- Dependencies and prerequisites\n\n**Business Impact**\n- Cost analysis (initial and ongoing)\n- Expected benefits and ROI\n- Risk assessment and mitigation\n- Scalability and future considerations\n\n**Trade-offs**\n- What you gain vs. what you sacrifice\n- Short-term vs. long-term implications\n- Reversibility of the decision\n\n**Recommendation**\n- Rank options with clear reasoning\n- Identify the best choice for your situation\n- Suggest next steps for implementation\n- Highlight any assumptions or unknowns\n\nUse a scoring matrix (1-5 scale) for objective comparison across criteria.\n\n[Describe your decision scenario and options here]",
            user: "mbeeson@fender.com",
            category: "Planning",
        },
        {
            title: "Code Explainer",
            prompt: "Please explain the following code in simple, easy-to-understand terms. Break down what each part does and how it works together:\n\nFor your explanation:\n- Start with a high-level overview of what the code accomplishes\n- Break down each section or function with clear explanations\n- Explain any complex logic, algorithms, or design patterns used\n- Identify the inputs, outputs, and side effects\n- Use analogies or examples when helpful\n- Highlight any potential issues or areas for improvement\n- Format your response with clear headings and bullet points\n\n[Paste your code here]",
            user: "mbeeson@fender.com",
            category: "Technical",
        },
        {
            title: "Unit Test Generator",
            prompt: "Write comprehensive unit tests for the following function. Include edge cases, error scenarios, and positive test cases:\n\nRequirements for the tests:\n- Use the same testing framework already used in the project (Jest, Mocha, etc.)\n- Include at least 5-7 test cases covering:\n  * Happy path scenarios with valid inputs\n  * Edge cases (empty values, null, undefined, boundary values)\n  * Error scenarios and exception handling\n  * Different input types and formats\n- Each test should have a descriptive name explaining what it tests\n- Include setup and teardown code if needed\n- Add comments explaining complex test scenarios\n- Ensure tests are independent and can run in any order\n\n[Paste your function code here]",
            user: "mbeeson@fender.com",
            category: "Technical",
        },
        {
            title: "Code Reviewer",
            prompt: "Please review the following code for potential bugs, performance issues, security vulnerabilities, and suggest improvements:\n\nFocus your review on:\n\n**Security:**\n- Input validation and sanitization\n- Authentication and authorization checks\n- Data exposure and logging concerns\n- Injection vulnerabilities (SQL, XSS, etc.)\n\n**Performance:**\n- Inefficient algorithms or data structures\n- Memory leaks or excessive memory usage\n- Network calls and database queries\n- Caching opportunities\n\n**Code Quality:**\n- Readability and maintainability\n- Error handling and edge cases\n- Code organization and structure\n- Naming conventions and documentation\n\n**Best Practices:**\n- Language-specific idioms and patterns\n- Framework or library usage\n- Testing coverage gaps\n\nProvide specific suggestions with code examples where applicable.\n\n[Paste your code here]",
            user: "mbeeson@fender.com",
            category: "Technical",
        },
        {
            title: "Idea Generator",
            prompt: "Generate creative and innovative ideas for solving this challenge. Think outside the box and consider unconventional approaches:\n\nChallenge Context:\n- Describe the problem or opportunity:\n- Current constraints or limitations:\n- Target audience or users:\n- Available resources:\n- Success criteria:\n\n**Ideation Approach:**\n\n**Traditional Solutions** (3-4 ideas)\n- Proven approaches from similar contexts\n- Industry best practices and standards\n- Incremental improvements to existing methods\n\n**Creative Alternatives** (4-5 ideas)\n- Cross-industry inspiration and analogies\n- Technology-enabled solutions\n- Process redesign or elimination approaches\n- Collaborative or community-driven methods\n\n**Disruptive Concepts** (2-3 ideas)\n- Complete reimagining of the problem\n- Opposite or contrarian approaches\n- Future-forward or emerging technology solutions\n\n**For each idea, include:**\n- Brief description (2-3 sentences)\n- Key benefits and unique value\n- Implementation difficulty (Low/Medium/High)\n- Resource requirements\n- Potential obstacles or concerns\n\n**Next Steps:**\n- Top 3 ideas worth prototyping or testing\n- Criteria for selecting the best approach\n- Suggested validation or research steps\n\n[Describe your challenge or opportunity here]",
            user: "mbeeson@fender.com",
            category: "Creative",
        },
        {
            title: "Solution Brainstormer",
            prompt: "Brainstorm multiple alternative solutions to this problem. Consider different perspectives, constraints, and desired outcomes:\n\n**Problem Definition:**\n- Core problem statement:\n- Who is affected and how:\n- Root causes (not just symptoms):\n- Current attempted solutions:\n- Desired outcome or success state:\n\n**Brainstorming Constraints:**\n- Budget limitations:\n- Time constraints:\n- Technical or resource limitations:\n- Stakeholder requirements:\n- Regulatory or compliance needs:\n\n**Solution Categories:**\n\n**Quick Wins** (Low effort, immediate impact)\n- Solutions implementable within days/weeks\n- Minimal resource requirements\n- Address symptoms while working on root causes\n\n**Strategic Solutions** (Medium effort, significant impact)\n- Balanced approach to effort vs. impact\n- 1-6 month implementation timeline\n- Address core issues systematically\n\n**Transformational Approaches** (High effort, game-changing impact)\n- Long-term, comprehensive solutions\n- Significant resource investment\n- Fundamentally change how the problem is approached\n\n**Alternative Perspectives:**\n- How would a startup approach this?\n- What would the customer/end-user prefer?\n- How do competitors or other industries solve similar problems?\n- What if budget/time weren't constraints?\n- What if we had to solve this with 10% of current resources?\n\n**Solution Evaluation:**\n- Rank top 5 solutions by feasibility and impact\n- Identify hybrid approaches combining multiple ideas\n- Suggest pilot or test approaches for validation\n\n[Describe your problem and context here]",
            user: "mbeeson@fender.com",
            category: "Creative",
        },
    ];
};

export const generateMockAssistantStream = (): ReadableStream<Uint8Array> => {
    const mockStreamData = [
        // Control messages
        { init_event_loop: true },
        { start: true },
        { start_event_loop: true },
        // Stream events
        {
            messageStart: {
                role: "assistant",
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: "The user is asking about how",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " Fender got its start -",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " this is likely",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " referring to the",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " Fender Musical",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " Instruments Corporation, the",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " famous",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " guitar an",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: "d ampl",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: "ifier company. To",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " provide",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " a comprehensive",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " answer about",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " Fender's origins, I should search",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " for information about the company's founding",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " an",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: "d early history.",
                    },
                },
            },
        },
        {
            contentBlockStop: {
                contentBlockIndex: 0,
            },
        },
        {
            contentBlockStart: {
                contentBlockIndex: 1,
                start: {
                    toolUse: {
                        name: "search",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    toolUse: {
                        input: "",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    toolUse: {
                        input: '{"q": "Fen',
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    toolUse: {
                        input: "der M",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    toolUse: {
                        input: "usi",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    toolUse: {
                        input: "cal Inst",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    toolUse: {
                        input: "rume",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    toolUse: {
                        input: "nts Corp",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    toolUse: {
                        input: "orat",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    toolUse: {
                        input: "ion histor",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    toolUse: {
                        input: "y fou",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    toolUse: {
                        input: "nding Leo Fe",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    toolUse: {
                        input: "nder",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    toolUse: {
                        input: '"}',
                    },
                },
            },
        },
        {
            contentBlockStop: {
                contentBlockIndex: 1,
            },
        },
        {
            messageStop: {
                stopReason: "tool_use",
            },
        },
        // Metadata event after messageStop
        {
            metadata: {
                usage: {
                    inputTokens: 120,
                    outputTokens: 85,
                    totalTokens: 205,
                },
                metrics: {
                    latencyMs: 2100,
                },
            },
        },
        // Complete message with contentid and messageid
        {
            message: {
                role: "assistant",
                content: [
                    {
                        reasoningContent: {
                            reasoningText: {
                                text: "The user is asking about how Fender got its start - this is likely referring to the Fender Musical Instruments Corporation, the famous guitar and amplifier company. To provide a comprehensive answer about Fender's origins, I should search for information about the company's founding and early history.",
                            },
                        },
                        contentid: "mock-content-001",
                    },
                    {
                        toolUse: {
                            name: "search",
                            toolUseId: "tooluse_search_123",
                            input: { q: "Fender Musical Instruments Corporation history founding Leo Fender" },
                        },
                        contentid: "mock-content-002",
                    },
                ],
                messageid: "mock-message-001",
            },
        },
        // User message with toolResult
        {
            message: {
                role: "user",
                content: [
                    {
                        toolResult: {
                            content: [
                                {
                                    json: {
                                        organic_results: [
                                            {
                                                title: "Leo Fender",
                                                snippet:
                                                    "Clarence Leonidas Fender (August 10, 1909 – March 21, 1991) was an American inventor and founder of the Fender Musical Instruments Corporation.",
                                                link: "https://en.wikipedia.org/wiki/Leo_Fender",
                                            },
                                            {
                                                title: "8 Things You Might Not Know About Leo Fender",
                                                snippet:
                                                    "More than 70 years ago, Leo Fender founded Fender and subsequently changed the face of music as we know it with countless inventions and innovations.",
                                                link: "https://www.fender.com/articles/behind-the-scenes/8-things-you-might-not-know-about-leo-fender",
                                            },
                                            {
                                                title: "Leo Fender: The History and Legacy of the Man Behind ...",
                                                snippet:
                                                    "Founded by former Fender associates Forrest White and Tom Walker, the company would become Music Man before Leo officially became president following the ...",
                                                link: "https://stringjoy.com/leo-fender/",
                                            },
                                            {
                                                title: "Leo Fender | Biography, Electric Guitars, & Facts",
                                                snippet:
                                                    "Leo Fender was an American inventor and manufacturer of electronic musical instruments. Together with George Fullerton, Fender developed the first ...",
                                                link: "https://www.britannica.com/biography/Leo-Fender",
                                            },
                                            {
                                                title: "TIL that Leo Fender, who founded Fender Musical ...",
                                                snippet:
                                                    "When he sold the Fender company in 1965 he signed a 10-year non-compete clause but helped start the guitar company Music Man as a silent partner ...",
                                                link: "https://www.reddit.com/r/todayilearned/comments/1lbmerp/til_that_leo_fender_who_founded_fender_musical/",
                                            },
                                            {
                                                title: "About FMIC: Fender Musical Instruments Corporation",
                                                snippet:
                                                    "The Fender story begins over 70 years ago in a small Fullerton, California workshop. A radio repairman by trade and inventor at heart, Leo Fender made the ...",
                                                link: "https://www.fender.com/pages/about-fmic",
                                            },
                                            {
                                                title: "Leo's Fender Story | G&L Musical Instruments",
                                                snippet:
                                                    "Leo Fender was a symbol of change and evolution for the benefit of musicians. But for any change that is considered, we must first ask ourselves, 'Would Leo ...",
                                                link: "https://www.glguitars.com/leos-story/",
                                            },
                                            {
                                                title: "Fender Timeline | The History Of Fender Guitars",
                                                snippet:
                                                    "1946 - Leo renamed his business to the Fender Electric Instruments Company. He turned his radio shop over to Dale Hyatt so he could concentrate ...",
                                                link: "https://www.soundunlimited.co.uk/blogs/articles/fender_timeline?srsltid=AfmBOooDQ0XYivbzejk-9nfPADp6hhIvCpdDVTP9jctS-GYKc0QQgMJe",
                                            },
                                            {
                                                title: "Leo Fender | EBSCO Research Starters",
                                                snippet:
                                                    "Leo Fender, an influential American inventor and entrepreneur, is best known for founding Fender Musical Instruments Corporation, a leading manufacturer of ...",
                                                link: "https://www.ebsco.com/research-starters/history/leo-fender",
                                            },
                                        ],
                                        related_questions: [
                                            {
                                                question: "What is the history of the Leo Fender?",
                                                snippet:
                                                    "Leo Fender (born August 10, 1909, Anaheim, California, U.S.—died March 21, 1991, Fullerton, California) was an American inventor and manufacturer of electronic musical instruments. Together with George Fullerton, Fender developed the first mass-produced solid-body electric guitar in 1951.",
                                            },
                                            { question: "Why did Leo Fender leave Fender?" },
                                            {
                                                question: "How much did Leo Fender sell Fender for?",
                                                snippet:
                                                    "Sale to CBS In January 1965, Leo Fender sold his companies to the Columbia Records Distribution Corporation, a subsidiary of the Columbia Broadcasting System (CBS), for $13 million ($130 million in 2024 adjusted for inflation).",
                                            },
                                            {
                                                question: "Did Leo Fender go deaf?",
                                                snippet:
                                                    "Bell: Speaking of disabilities, Leo had a glass eye. His eye was damaged when he was seven or eight years old. He was deaf because he had his head in an amplifier and someone turned it on while he was trying to fix it and blew out his hearing.",
                                            },
                                        ],
                                    },
                                },
                            ],
                            toolUseId: "tooluse_search_123",
                            status: "success",
                        },
                        contentid: "mock-content-003",
                    },
                ],
                messageid: "mock-message-002",
            },
        },
        // Control messages before next message stream
        { start: true },
        { start: true },
        { start_event_loop: true },
        {
            messageStart: {
                role: "assistant",
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: "The search",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " results give",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " me",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " goo",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: "d basic",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " information about how",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " Fender got starte",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: "d. I",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " can see that Leo Fender was the",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " founder,",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " he starte",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: "d the company over",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " 70 years ago (",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: "so",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " around the",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " 1950s)",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " in",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " Fullerton, California,",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " and he was originally",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " a radio repairman. Let",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " me get",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " more detaile",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: "d information by",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " looking at the official",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " Fender company",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " page about",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " their history.",
                    },
                },
            },
        },
        {
            contentBlockStop: {
                contentBlockIndex: 0,
            },
        },
        {
            contentBlockStart: {
                contentBlockIndex: 1,
                start: {
                    toolUse: {
                        name: "get_webpage",
                        toolUseId: "tooluse_dGFMpCPGS-OzeCK8BA5Ygg",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    toolUse: {
                        input: "",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    toolUse: {
                        input: '{"url": "htt',
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    toolUse: {
                        input: "ps://w",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    toolUse: {
                        input: "ww.fender.c",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    toolUse: {
                        input: "om/pages",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    toolUse: {
                        input: "/about-",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    toolUse: {
                        input: "fmic",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    toolUse: {
                        input: '"}',
                    },
                },
            },
        },
        {
            contentBlockStop: {
                contentBlockIndex: 1,
            },
        },
        {
            messageStop: {
                stopReason: "tool_use",
            },
        },
        // Metadata event after messageStop
        {
            metadata: {
                usage: {
                    inputTokens: 140,
                    outputTokens: 95,
                    totalTokens: 235,
                },
                metrics: {
                    latencyMs: 2300,
                },
            },
        },
        // Complete message for second assistant message
        {
            message: {
                role: "assistant",
                content: [
                    {
                        reasoningContent: {
                            reasoningText: {
                                text: "The search results give me good basic information about how Fender got started. I can see that Leo Fender was the founder, he started the company over 70 years ago (so around the 1950s) in Fullerton, California, and he was originally a radio repairman. Let me get more detailed information by looking at the official Fender company page about their history.",
                            },
                        },
                        contentid: "mock-content-004-reasoning",
                    },
                    {
                        toolUse: {
                            name: "get_webpage",
                            toolUseId: "tooluse_webpage_456",
                            input: { url: "https://www.fender.com/pages/about-fmic" },
                        },
                        contentid: "mock-content-004-tool",
                    },
                ],
                messageid: "mock-message-004",
            },
        },
        // User message with toolResult (error response)
        {
            message: {
                role: "user",
                content: [
                    {
                        toolResult: {
                            toolUseId: "tooluse_webpage_456",
                            status: "error",
                            content: [
                                {
                                    text: "error, try another page",
                                },
                            ],
                        },
                        contentid: "mock-content-005",
                    },
                ],
                messageid: "mock-message-005",
            },
        },
        // Control message before next assistant stream
        { start: true },
        {
            messageStart: {
                role: "assistant",
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: "The",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " Fender page",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " didn",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: "'t work,",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " so let me try",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " the",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " Wikipedia",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " page about Leo Fender to",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " get more detailed information about how",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " the",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " company starte",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: "d.",
                    },
                },
            },
        },
        {
            contentBlockStop: {
                contentBlockIndex: 0,
            },
        },
        {
            contentBlockStart: {
                contentBlockIndex: 1,
                start: {
                    toolUse: {
                        name: "get_webpage",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    toolUse: {
                        input: "",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    toolUse: {
                        input: '{"url": ',
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    toolUse: {
                        input: '"https',
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    toolUse: {
                        input: "://en.wik",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    toolUse: {
                        input: "ipedia.org",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    toolUse: {
                        input: "/wiki/Le",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    toolUse: {
                        input: "o_Fen",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    toolUse: {
                        input: "der",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    toolUse: {
                        input: '"}',
                    },
                },
            },
        },
        {
            contentBlockStop: {
                contentBlockIndex: 1,
            },
        },
        {
            messageStop: {
                stopReason: "tool_use",
            },
        },
        // Metadata event after messageStop
        {
            metadata: {
                usage: {
                    inputTokens: 160,
                    outputTokens: 105,
                    totalTokens: 265,
                },
                metrics: {
                    latencyMs: 2500,
                },
            },
        },
        // Complete message for third assistant message
        {
            message: {
                role: "assistant",
                content: [
                    {
                        reasoningContent: {
                            reasoningText: {
                                text: "The Fender page didn't work, so let me try the Wikipedia page about Leo Fender to get more detailed information about how the company started.",
                            },
                        },
                        contentid: "mock-content-006-reasoning",
                    },
                    {
                        toolUse: {
                            name: "get_webpage",
                            toolUseId: "tooluse_webpage_789",
                            input: { url: "https://en.wikipedia.org/wiki/Leo_Fender" },
                        },
                        contentid: "mock-content-006-tool",
                    },
                ],
                messageid: "mock-message-006",
            },
        },
        // User message with toolResult (success response)
        {
            message: {
                role: "user",
                content: [
                    {
                        toolResult: {
                            toolUseId: "tooluse_webpage_789",
                            status: "success",
                            content: [
                                {
                                    text: "Jump to content Main menu Main menu move to sidebar hide Navigation  * Main page  * Contents  * Current events * Random article * About Wikipedia * Contact us",
                                },
                            ],
                        },
                        contentid: "mock-content-007",
                    },
                ],
                messageid: "mock-message-007",
            },
        },
        // Control message before next assistant stream
        { start: true },
        {
            messageStart: {
                role: "assistant",
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: "Perfect",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: "! I now",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " have comprehensive",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " information about how",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " Fender got",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " its start",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " from",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " the",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " Wikipedia",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " page",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: ". Let me summarize the",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " key points about",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " the",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " founding an",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: "d early history of Fender:",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: "\n\n1. Leo",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " Fender was born in 1",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: "909 an",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: "d was originally",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " intereste",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: "d in electronics",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " and radio repair",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: "\n2. He starte",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: "d Fender Radio",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " Service",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " in 1938",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " as",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " a radio",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " repair shop\n3. Musicians",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " starte",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: "d coming to him for",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " PA",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " systems",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " and amplification",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: "\n4. In 1944",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: "-",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: "1946",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: ", he partn",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: "ered with Doc",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " Kauffman to form",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " K",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: "&",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: "F Manufacturing",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " Corporation",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: "\n5. In",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " 1946",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: ", he rename",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: "d it",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " to Fender Electric",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " Instrument",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " Co",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: ". \n6. He",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " develope",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: "d the first mass",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: "-produced soli",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: "d-body electric guitars",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " (",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: "Telec",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: "aster,",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " Strat",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: "ocaster)",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " an",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: "d the",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " Precision Bass\n7. The company",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " was eventually",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " sold to CBS in 1965",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: "\n\nThis gives",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " me a complete picture of Fender",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: "'s origins",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " to",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " share",
                    },
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 0,
                delta: {
                    reasoningContent: {
                        text: " with the user.",
                    },
                },
            },
        },
        {
            contentBlockStop: {
                contentBlockIndex: 0,
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "Based on the information",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " I found,",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " here's how",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " Fender got its start:",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "\n\n**The",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " Founding Story",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "**",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "\n\nFender was founde",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "d by",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " Leo Fender (",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "Clarence",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " Leonidas Fender,",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " ",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "1909-1991), who",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " was originally",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " a",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " radio rep",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "airman,",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " not",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " a guitarist",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " himself",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: ". The",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " company's",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " origins",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " trace back to 1938 when",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " Leo,",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " with",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " a",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " borrowe",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "d $",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "600",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: ",",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " started **",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "Fender Radio Service** in",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " Fullerton, California,",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " as",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " a radio repair shop.\n\n**The",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " Path",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " to Musical",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " Instruments**",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "\n\nAs Leo",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "'s",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " radio",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " repair business grew",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: ", musicians",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " an",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "d ban",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "dleaders began",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " approaching",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " him to",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " buil",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "d,",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " rent",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: ", an",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "d sell",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " public",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " address (PA) systems.",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " This",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " naturally",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " led him",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " into",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " the worl",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "d of musical",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " instrument",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " ampl",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "ification,",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " as",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " musicians",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " needed ampl",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "ification for acoustic",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " guitars",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " and the",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " emerging",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: ' electric "',
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "Hawaiian",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: '" or',
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " lap",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " steel guitars popular",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " in country",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " music.\n\n**Key",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " Partnerships",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " an",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "d Evolution",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "**\n\nDuring",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " Worl",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "d War II, Leo met",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " Clayton",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: ' "',
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: 'Doc" Kauffman, an',
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " inventor who had worke",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "d for",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " Rick",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "enbacker. In",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " 1944, they formed **",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "K&F Manufacturing Corporation** to design",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " and buil",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "d ampl",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "ifie",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "d Hawaiian",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " guitars an",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "d ampl",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "ifiers. However",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: ", in 1946, Doc",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " pulle",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "d out of",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " the partnership.",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "\n\nLeo",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " then revise",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "d an",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "d renamed the company,",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " first",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: ' to "',
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "Fender Manufacturing",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: ',"',
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " an",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "d later to **",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: '"',
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "Fender Electric Instrument Co",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: '."** at',
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " the end of 1947",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: ". He",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " hande",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "d over his",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " radio",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " shop",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " to Dale",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " Hyatt to",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " focus",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " entirely",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " on musical",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " instruments.\n\n**Revolutionary",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " Innovations**\n\nRecogn",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "izing the need for lou",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "der,",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " cheaper",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: ",",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " and more ",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "durable instruments",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " for",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " the growing dance",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " hall",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " and roa",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "dhouse scene",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: ", Leo developed:",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "\n\n- **",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "1",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "950",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "**:",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " The",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " F",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "ender Esqu",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "ire (",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "single",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " pickup",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: ")",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " an",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "d Telec",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "aster (two pickups)",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " - among",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " the first mass-produced solid-body",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " electric guitars\n- **1951",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "**: The Precision Bass - revolut",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "ionizing bass",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " playing",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " by",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " making",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " it lou",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "der and more",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " portable",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " than",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " acoustic",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " double",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " basses\n- **1954",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "**: The iconic",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " Stratocaster with",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " its innovative",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " three",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "-pickup",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " design and tremolo arm",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "\n\nLeo",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " Fender's genius",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " was",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " identifying",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " the practical",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " needs of working",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " musicians and creating innovative",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: ",",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " mass",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "-produc",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "ible solutions",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: ".",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " Despite",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " not",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " being",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " a guitarist himself, he create",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "d some",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " of the most iconic instruments in music",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " history,",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " fundament",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "ally changing popular",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " music.",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " The company was",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " eventually sold to CBS in 1965",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " ",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: "for $13",
                },
            },
        },
        {
            contentBlockDelta: {
                contentBlockIndex: 1,
                delta: {
                    text: " million.",
                },
            },
        },
        {
            contentBlockStop: {
                contentBlockIndex: 1,
            },
        },
        {
            messageStop: {
                stopReason: "end_turn",
            },
        },
        // Metadata event after final messageStop
        {
            metadata: {
                usage: {
                    inputTokens: 150,
                    outputTokens: 850,
                    totalTokens: 1000,
                },
                metrics: {
                    latencyMs: 3500,
                },
            },
        },
        // Complete final message with contentid and messageid
        {
            message: {
                role: "assistant",
                content: [
                    {
                        reasoningContent: {
                            reasoningText: {
                                text: "The Wikipedia page provided good information about Leo Fender. I now have enough information to provide a comprehensive answer about how Fender got its start. Let me compile this into a well-structured response covering the founding story, the path to musical instruments, key partnerships and evolution, and the revolutionary innovations.",
                            },
                        },
                        contentid: "mock-content-008-reasoning",
                    },
                    {
                        text: 'Based on the information I found, here\'s how Fender got its start:\n\n**The Founding Story**\n\nFender was founded by Leo Fender (Clarence Leonidas Fender, 1909-1991), who was originally a radio repairman, not a guitarist himself. The company\'s origins trace back to 1938 when Leo, with a borrowed $600, started **Fender Radio Service** in Fullerton, California, as a radio repair shop.\n\n**The Path to Musical Instruments**\n\nAs Leo\'s radio repair business grew, musicians and bandleaders began approaching him to build, rent, and sell public address (PA) systems. This naturally led him into the world of musical instrument amplification, as musicians needed amplification for acoustic guitars and the emerging electric "Hawaiian" or lap steel guitars popular in country music.\n\n**Key Partnerships and Evolution**\n\nDuring World War II, Leo met Clayton "Doc" Kauffman, an inventor who had worked for Rickenbacker. In 1944, they formed **K&F Manufacturing Corporation** to design and build amplified Hawaiian guitars and amplifiers. However, in 1946, Doc pulled out of the partnership.\n\nLeo then revised and renamed the company, first to "Fender Manufacturing," and later to **"Fender Electric Instrument Co."** at the end of 1947. He handed over his radio shop to Dale Hyatt to focus entirely on musical instruments.\n\n**Revolutionary Innovations**\n\nRecognizing the need for louder, cheaper, and more durable instruments for the growing dance hall and roadhouse scene, Leo developed:\n\n- **1950**: The Fender Esquire (single pickup) and Telecaster (two pickups) - among the first mass-produced solid-body electric guitars\n- **1951**: The Precision Bass - revolutionizing bass playing by making it louder and more portable than acoustic double basses\n- **1954**: The iconic Stratocaster with its innovative three-pickup design and tremolo arm\n\nLeo Fender\'s genius was identifying the practical needs of working musicians and creating innovative, mass-producible solutions. Despite not being a guitarist himself, he created some of the most iconic instruments in music history, fundamentally changing popular music. The company was eventually sold to CBS in 1965 for $13 million.',
                        contentid: "mock-content-008-text",
                    },
                ],
                messageid: "mock-message-008",
            },
        },
    ];

    let index = 0;

    return new ReadableStream<Uint8Array>({
        start(controller) {
            const writeNext = () => {
                if (index < mockStreamData.length) {
                    const item = mockStreamData[index];
                    // Control messages and complete messages don't get wrapped in event
                    const dataToSend =
                        item.init_event_loop || item.start || item.start_event_loop || item.message
                            ? item
                            : { event: item };

                    // Format as SSE: "data: {json}\n"
                    const chunk = "data: " + JSON.stringify(dataToSend) + "\n";
                    const encoder = new TextEncoder();
                    controller.enqueue(encoder.encode(chunk));
                    index++;
                    setTimeout(writeNext, 50);
                } else {
                    controller.close();
                }
            };
            writeNext();
        },
    });
};

export const generateMockSummary = (prompt: string): ISummary => {
    return { summary: `Test Summary ${Date.now()} ${prompt.length}` };
};
